import { supabase } from './supabase';
import type { ApiResponse } from './appConfigService';

export type TelemetryLevel = 'error' | 'warning' | 'info';

export interface TelemetryEventRecord {
  id: string;
  created_at: string;
  user_id?: string | null;
  session_id?: string | null;
  level: TelemetryLevel;
  event_name: string;
  message?: string | null;
  source?: string | null;
  platform?: string | null;
  app_version?: string | null;
  url?: string | null;
  user_agent?: string | null;
  tags?: Record<string, unknown> | null;
  payload?: Record<string, unknown> | null;
  context?: Record<string, unknown> | null;
}

export interface MonitoringAlertRecord {
  id: string;
  created_at: string;
  updated_at: string;
  status: 'open' | 'acknowledged' | 'resolved';
  severity: 'info' | 'warning' | 'error' | 'critical';
  title: string;
  description?: string | null;
  source: string;
  dedupe_key?: string | null;
  first_seen_at: string;
  last_seen_at: string;
  occurrence_count: number;
  metadata?: Record<string, unknown> | null;
  acknowledged_by?: string | null;
  acknowledged_at?: string | null;
  resolved_by?: string | null;
  resolved_at?: string | null;
}

export interface MonitoringAlertRunRecord {
  id: string;
  created_at: string;
  detector_name: string;
  outcome: 'success' | 'error' | 'partial';
  duration_ms?: number | null;
  alerts_created: number;
  alerts_updated: number;
  summary?: Record<string, unknown> | null;
  error?: string | null;
}

export interface SessionLossEnforcementSummary {
  reason: string;
  count: number;
  last_seen_at: string;
  /** Highest-severity outcome seen for this path (fatal > error > suppressed). */
  severity?: 'fatal' | 'error' | 'suppressed' | 'legacy';
  failureClass?: string | null;
  /** True only for classified invalid_session fatals. Rotation races and locked-store writes never logout. */
  logoutEnforced?: boolean;
}

export type TelemetryEventInsert = Omit<TelemetryEventRecord, 'id' | 'created_at'>;

export const telemetryMonitorService = {
  async logTelemetryEvent(payload: TelemetryEventInsert): Promise<ApiResponse<null>> {
    try {
      const { error } = await supabase.from('app_telemetry_events').insert([payload]);
      if (error) {
        return { success: false, message: error.message };
      }
      return { success: true, data: null };
    } catch (error: any) {
      return { success: false, message: error?.message || 'Failed to log telemetry event.' };
    }
  },

  async getTelemetryEventsPage(
    limit = 50,
    offset = 0,
    options?: { level?: TelemetryLevel; source?: string; eventName?: string; search?: string },
  ): Promise<ApiResponse<TelemetryEventRecord[]> & { count?: number | null }> {
    try {
      let query = supabase
        .from('app_telemetry_events')
        .select(
          'id, created_at, user_id, session_id, level, event_name, message, source, platform, app_version, url, user_agent, tags, payload, context',
          { count: 'exact' },
        )
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (options?.level) query = query.eq('level', options.level);
      if (options?.source) query = query.eq('source', options.source);
      if (options?.eventName) query = query.eq('event_name', options.eventName);
      if (options?.search) {
        query = query.or(`message.ilike.%${options.search}%,event_name.ilike.%${options.search}%`);
      }

      const { data, error, count } = await query;
      if (error) {
        return { success: false, message: error.message, count };
      }
      return { success: true, data: data ?? [], count };
    } catch (error: any) {
      return { success: false, message: error?.message || 'Failed to load telemetry events.' };
    }
  },

  async getRecentSevereTelemetryEvents(limit = 500): Promise<ApiResponse<TelemetryEventRecord[]>> {
    try {
      const { data, error } = await supabase
        .from('app_telemetry_events')
        .select(
          'id, created_at, user_id, session_id, level, event_name, message, source, platform, app_version, url, user_agent, tags, payload, context',
        )
        .in('level', ['error', 'warning'])
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        return { success: false, message: error.message };
      }
      return { success: true, data: data ?? [] };
    } catch (error: any) {
      return { success: false, message: error?.message || 'Failed to load recent telemetry events.' };
    }
  },

  async getSessionLossEnforcementSummary(
    limit = 250,
  ): Promise<ApiResponse<SessionLossEnforcementSummary[]>> {
    try {
      const { data, error } = await supabase
        .from('app_telemetry_events')
        .select('created_at, payload, source')
        .eq('event_name', 'auth-session-refresh')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        return { success: false, message: error.message };
      }

      const severityRank = (value?: SessionLossEnforcementSummary['severity']) => {
        if (value === 'fatal') return 3;
        if (value === 'error') return 2;
        if (value === 'suppressed') return 1;
        if (value === 'legacy') return 2;
        return 0;
      };

      const breakdown = new Map<string, SessionLossEnforcementSummary>();
      (data ?? []).forEach((row: { created_at: string; payload?: Record<string, unknown> | null; source?: string | null }) => {
        if (row.source && row.source !== 'mobile.telemetry') return;
        const payload = row.payload ?? {};
        const rawReason = typeof payload.reason === 'string' ? payload.reason : '';
        const outcome = typeof payload.outcome === 'string' ? payload.outcome : '';
        const label = typeof payload.label === 'string' ? payload.label : 'unknown-path';
        const failureClass =
          typeof payload.failureClass === 'string' ? payload.failureClass : null;
        const refreshPath =
          typeof payload.refreshPath === 'string' ? payload.refreshPath : null;

        const isLegacyEnforced = rawReason.startsWith('session-loss-enforced:');
        // In-flight SDK SIGNED_OUT during refreshSession is not an enforced logout.
        const isSdkSignedOutHintNoise =
          rawReason.startsWith('signed-out-source:') &&
          failureClass !== 'invalid_session' &&
          outcome !== 'fatal';
        const isActionableOutcome =
          outcome === 'fatal' ||
          outcome === 'error' ||
          (outcome === 'suppressed' &&
            (failureClass === 'timeout' ||
              failureClass === 'invalid_session' ||
              failureClass === 'rotation_race' ||
              rawReason.startsWith('transient:secure-store')));

        if (isSdkSignedOutHintNoise) return;
        if (!isLegacyEnforced && !isActionableOutcome) return;

        const stableReason = isLegacyEnforced
          ? rawReason.replace('session-loss-enforced:', '').trim() || 'unknown'
          : rawReason || outcome || 'unknown';
        const classPrefix = failureClass ? `${failureClass}/` : '';
        const pathSuffix = refreshPath ? ` [${refreshPath}]` : '';
        const reason = `${classPrefix}${stableReason} @ ${label}${pathSuffix}`;
        const severity: SessionLossEnforcementSummary['severity'] = isLegacyEnforced
          ? 'legacy'
          : outcome === 'fatal'
            ? 'fatal'
            : outcome === 'suppressed'
              ? 'suppressed'
              : 'error';
        const logoutEnforced =
          isLegacyEnforced || (outcome === 'fatal' && failureClass === 'invalid_session');

        const existing = breakdown.get(reason);
        if (existing) {
          existing.count += 1;
          if (new Date(row.created_at).getTime() > new Date(existing.last_seen_at).getTime()) {
            existing.last_seen_at = row.created_at;
          }
          if (severityRank(severity) > severityRank(existing.severity)) {
            existing.severity = severity;
          }
          if (!existing.failureClass && failureClass) {
            existing.failureClass = failureClass;
          }
          if (logoutEnforced) {
            existing.logoutEnforced = true;
          }
          return;
        }
        breakdown.set(reason, {
          reason,
          count: 1,
          last_seen_at: row.created_at,
          severity,
          failureClass,
          logoutEnforced,
        });
      });

      return {
        success: true,
        data: Array.from(breakdown.values()).sort((a, b) => {
          if (b.count !== a.count) return b.count - a.count;
          return new Date(b.last_seen_at).getTime() - new Date(a.last_seen_at).getTime();
        }),
      };
    } catch (error: any) {
      return {
        success: false,
        message: error?.message || 'Failed to load session-loss enforcement summary.',
      };
    }
  },

  async getAlertsPage(
    limit = 50,
    offset = 0,
    options?: { status?: MonitoringAlertRecord['status']; severity?: MonitoringAlertRecord['severity'] },
  ): Promise<ApiResponse<MonitoringAlertRecord[]> & { count?: number | null }> {
    try {
      let query = supabase
        .from('monitoring_alerts')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (options?.status) query = query.eq('status', options.status);
      if (options?.severity) query = query.eq('severity', options.severity);

      const { data, error, count } = await query;
      if (error) {
        return { success: false, message: error.message, count };
      }
      return { success: true, data: (data ?? []) as MonitoringAlertRecord[], count };
    } catch (error: any) {
      return { success: false, message: error?.message || 'Failed to load monitoring alerts.' };
    }
  },

  async getRecentAlertRuns(limit = 25): Promise<ApiResponse<MonitoringAlertRunRecord[]>> {
    try {
      const { data, error } = await supabase
        .from('monitoring_alert_runs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) {
        return { success: false, message: error.message };
      }
      return { success: true, data: (data ?? []) as MonitoringAlertRunRecord[] };
    } catch (error: any) {
      return { success: false, message: error?.message || 'Failed to load monitoring alert runs.' };
    }
  },

  async acknowledgeAlert(id: string): Promise<ApiResponse<null>> {
    try {
      const { data: auth } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('monitoring_alerts')
        .update({
          status: 'acknowledged',
          acknowledged_by: auth.user?.id ?? null,
          acknowledged_at: new Date().toISOString(),
        })
        .eq('id', id);
      if (error) {
        return { success: false, message: error.message };
      }
      return { success: true, data: null };
    } catch (error: any) {
      return { success: false, message: error?.message || 'Failed to acknowledge alert.' };
    }
  },

  async resolveAlert(id: string): Promise<ApiResponse<null>> {
    try {
      const { data: auth } = await supabase.auth.getUser();
      const nowIso = new Date().toISOString();
      const { error } = await supabase
        .from('monitoring_alerts')
        .update({
          status: 'resolved',
          resolved_by: auth.user?.id ?? null,
          resolved_at: nowIso,
          acknowledged_by: auth.user?.id ?? null,
          acknowledged_at: nowIso,
        })
        .eq('id', id);
      if (error) {
        return { success: false, message: error.message };
      }
      return { success: true, data: null };
    } catch (error: any) {
      return { success: false, message: error?.message || 'Failed to resolve alert.' };
    }
  },
};
